import {
    Box,
    HStack,
    IconButton,
    Menu,
    MenuButton,
    Portal,
    Tooltip,
} from '@chakra-ui/react';
import { useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { MenuList, MenuItem } from 'components/v4';
import { PlatformEnums } from 'utils/platforms';
import PlatformCellRenderer from './CustomCells/PlatformCellRenderer';
import {
    STACK_PREVIEW_COUNT,
    PLATFORM_STACK_MIN_WIDTH,
    getPlatformStackFullWidth,
} from './platformFilterStackSizing';

/**
 * (2951) The GCC High logo is a square flag, which reads as a hard-edged tile
 * beside the round platform logos in the filter. Crop it to a circle so the
 * row sits together; every other logo is already round or transparent.
 */
const FilterPlatformIcon = ({ platform }) => {
    const icon = (
        <PlatformCellRenderer platformName={false} value={platform.id} />
    );

    if (platform.id !== PlatformEnums.GCC) {
        return icon;
    }

    return (
        <Box boxSize="7" borderRadius="full" overflow="hidden">
            {icon}
        </Box>
    );
};

/**
 * Row of platform filter icons for an AGGridMain toolbar. Collapses into a
 * small overlapping icon "stack" that opens a dropdown menu when the full
 * row of icons no longer fits the available toolbar width.
 *
 * By default this measures its own available width and decides for itself.
 * Pass `forceOverflowing` (a boolean) when a parent toolbar coordinates this
 * component's collapse state alongside a sibling (e.g. a button group) that
 * shares the same row — in that case this component skips its own
 * measurement and simply renders whichever state the parent has decided.
 */
const PlatformFilterStack = ({
    platformList,
    isSelected,
    onSelect,
    forceOverflowing,
}) => {
    const { darkMode } = useSelector((state) => state.settings);
    const containerRef = useRef(null);
    const [autoOverflowing, setAutoOverflowing] = useState(false);
    const isControlled = forceOverflowing !== undefined;
    const isOverflowing = isControlled ? forceOverflowing : autoOverflowing;

    useLayoutEffect(() => {
        if (isControlled) {
            return undefined;
        }

        const container = containerRef.current;
        if (!container || !platformList?.length) {
            setAutoOverflowing(false);
            return undefined;
        }

        const neededWidth = getPlatformStackFullWidth(platformList.length);

        const checkOverflow = () => {
            const availableWidth = container.clientWidth;
            // A width of 0 means the container hasn't been laid out yet
            // (or layout isn't available, e.g. in jsdom) — skip the check
            // rather than falsely collapsing into the overflow menu.
            if (availableWidth > 0) {
                setAutoOverflowing(availableWidth < neededWidth);
            }
        };

        checkOverflow();

        const resizeObserver = new ResizeObserver(checkOverflow);
        resizeObserver.observe(container);
        return () => resizeObserver.disconnect();
    }, [platformList, isControlled]);

    if (!platformList?.length || platformList.length <= 1) {
        return null;
    }

    // Show the currently selected platform at the front of the collapsed
    // stack (and always within the preview), keeping the rest in order.
    const selectedIndex = platformList.findIndex((platform) =>
        isSelected(platform),
    );
    const stackList =
        selectedIndex > 0
            ? [
                  platformList[selectedIndex],
                  ...platformList.filter((_, index) => index !== selectedIndex),
              ]
            : platformList;
    // While a filter is active the whole stack sits on a highlighted pill.
    // The rings separating the overlapping icons have to take the highlight
    // colour too, otherwise they cut the pill into disconnected slivers
    // instead of reading as one highlighted surface.
    const hasSelection = selectedIndex !== -1;
    const highlightBg = darkMode ? 'blue.200' : 'blue.100';
    const iconRingColor = hasSelection
        ? highlightBg
        : darkMode
          ? 'gray.800'
          : 'white';
    const overflowBadgeBg = hasSelection
        ? 'blue.300'
        : darkMode
          ? 'gray.600'
          : 'gray.100';
    const overflowBadgeColor = hasSelection
        ? 'gray.800'
        : darkMode
          ? 'white'
          : 'gray.700';

    return (
        <Box
            ref={containerRef}
            data-testid="platform-filter-container"
            flexGrow={isControlled ? 0 : 0}
            flexShrink={isControlled ? 0 : 1}
            minWidth={
                isControlled ? undefined : `${PLATFORM_STACK_MIN_WIDTH}px`
            }
            overflow="hidden">
            {isOverflowing ? (
                <Menu>
                    <Tooltip
                        label="Platform Filters"
                        placement="bottom"
                        hasArrow>
                        <MenuButton
                            as="button"
                            type="button"
                            aria-label="Platform filters"
                            data-selected={hasSelection ? '' : undefined}
                            display="flex"
                            alignItems="center"
                            // The padding is what leaves room for the highlight
                            // to show around the icons — its horizontal part is
                            // accounted for in PLATFORM_STACK_MIN_WIDTH.
                            bg={hasSelection ? highlightBg : 'transparent'}
                            borderRadius="full"
                            paddingX="2"
                            paddingY="1"
                            border="none"
                            cursor="pointer">
                            <HStack spacing="-5">
                                {stackList
                                    .slice(0, STACK_PREVIEW_COUNT)
                                    .map((platform, index) => (
                                        <Box
                                            key={platform.id}
                                            boxSize="8"
                                            borderRadius="full"
                                            border="2px solid"
                                            borderColor={iconRingColor}
                                            bg={darkMode ? 'gray.700' : 'white'}
                                            overflow="hidden"
                                            zIndex={
                                                STACK_PREVIEW_COUNT - index
                                            }>
                                            <PlatformCellRenderer
                                                platformName={false}
                                                value={platform.id}
                                            />
                                        </Box>
                                    ))}
                                {platformList.length > STACK_PREVIEW_COUNT && (
                                    <Box
                                        borderRadius="full"
                                        border="2px solid"
                                        borderColor={iconRingColor}
                                        bg={overflowBadgeBg}
                                        boxSize="8"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        fontSize="xs"
                                        fontWeight="bold"
                                        color={overflowBadgeColor}
                                        zIndex={0}>
                                        +
                                        {platformList.length -
                                            STACK_PREVIEW_COUNT}
                                    </Box>
                                )}
                            </HStack>
                        </MenuButton>
                    </Tooltip>
                    {/* Portalled so this component's own overflow:hidden
                        (and the toolbar's) can't clip the dropdown. */}
                    <Portal>
                        <MenuList
                            zIndex={1000}
                            bg={darkMode ? 'gray.700' : 'white'}>
                            {platformList.map((platform) => (
                                <MenuItem
                                    key={platform.id}
                                    icon={
                                        <FilterPlatformIcon
                                            platform={platform}
                                        />
                                    }
                                    bg={
                                        isSelected(platform)
                                            ? darkMode
                                                ? 'gray.600'
                                                : 'gray.100'
                                            : undefined
                                    }
                                    onClick={() => onSelect(platform)}>
                                    {platform.name}
                                </MenuItem>
                            ))}
                        </MenuList>
                    </Portal>
                </Menu>
            ) : (
                <HStack spacing={2}>
                    {platformList.map((platform) => (
                        <IconButton
                            key={platform.id}
                            icon={<FilterPlatformIcon platform={platform} />}
                            aria-label={platform.name}
                            size="sm"
                            variant="ghost"
                            opacity={isSelected(platform) ? 1 : 0.5}
                            onClick={() => onSelect(platform)}
                        />
                    ))}
                </HStack>
            )}
        </Box>
    );
};

export default PlatformFilterStack;
